import React, { useContext, useMemo, useState } from 'react';
import { CCard, CDataTable, CCollapse, CTooltip, CImg, CBadge } from '@coreui/react';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { Job, JobPhase, JobPhaseColor, JobPhaseLabel } from '../../shared/models/jobs';
import { UserInfo } from '../../shared/models/user';
import JobActions from '../../jobs/components/JobActions';
import tooltip from '../../assets/images/tool_tip.svg';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import TableStats from '../../shared/components/TableStats';
import JobsDetailsContainer from './JobsDetailsContainer';

import diffAsString from '../../shared/utils/time-utils';
import { COLUMN_SIZES } from './JobList';
import FeedbackRating from '../../shared/components/FeedbackRating';
import { selectOrgList } from '../../store/selectors/orgHierarchy.selectors';
import { formatJobDescription } from '../../shared/utils/job-utils';

const FIELDS = ({ agent }: { agent: string}, enableOrgHierarchyModule, isSessionsEnabled) => [
  {
    key: 'technician',
    label: agent,
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'feedback',
    label: <div className="text-nowrap">Feedback
      <CTooltip placement="top" content="Customer feedback. 5-star rating system">
        <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableStatus"/>
      </CTooltip>
    </div>,
    _classes: ['text-center'],
  },
  {
    key: 'wait_time',
    label: 'Wait Time',
  },
  ...(enableOrgHierarchyModule ? [{
    key: 'org_name',
    label: 'Org Name',
    filter: false,
    sorter: false,
  }] : []),
  ...(isSessionsEnabled ? [{
    key: 'session_id',
    label: 'Session ID',
  }, {
    key: 'rank',
    label: 'Session Rank'
  }] : []),
  {
    key: 'actions',
    label: '',
    sorter: false,
    filter: false,
  },
];

export interface CompletedJobListProps {
  title: string;
  jobs: Job[];
  technicians: UserInfo[];
  loading: boolean;
  isSessionsEnabled?: boolean;
}

const CompletedJobList = (props: CompletedJobListProps) => {
  const { title, jobs, technicians, loading, isSessionsEnabled = false } = props;
  const {
    enableOrgHierarchyModule
  } = useFlags();
  const labels = useContext(ConfigContext);
  const orgs = useSelector(selectOrgList);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState<string>(undefined);

  const handleRowClick = ({ invite_code: inviteCode }: Job, index, columnName, e) => {
    if (columnName !== 'actions' || e.target.tagName.toLowerCase() === 'td') {
      setShowDrawer(showDrawer !== inviteCode ? inviteCode : undefined);
    }
  };

  const sortedJobs = useMemo(() => {
    return jobs?.map(job => {
      return {
        ...job,
        org_name: orgs?.find(value => value?._id === job?.org_id)?.name ?? '',
      };
    })?.sort((a, b) => {
      if (a.session_id === b.session_id) {
        return a.rank > b.rank ? 1 : -1;
      }
      return 0;
    });
  }, [jobs]);

  return (
    <div>
      <div className='glympser-cardHeader'>
        <div className='glympser-cardTitle'>
          {title}
        </div>
        <CTooltip placement="top" content="Orders completed or canceled today">
          <CImg src={tooltip} className="glympser-toolTip"/>
        </CTooltip>
      </div>
      <CCard className="glympser-container--roundedWithShadow">
        <div className='job-list-container'>
          <CDataTable
            fields={ FIELDS(labels, enableOrgHierarchyModule, isSessionsEnabled) }
            items={ sortedJobs }
            loading={ loading }
            clickableRows
            striped
            responsive
            noItemsViewSlot={<div className='glympse-noItemsMsg'>No Items</div>}
            onRowClick={ handleRowClick }
            pagination={{
              addListClass: "ml-2"
            }}
            onPageChange={(page) => setCurrentPage(page)}
            underTableSlot={jobs && jobs.length > 0 && <TableStats page={currentPage} total={jobs.length} />}
            scopedSlots={{
              'technician': ({ agent_id }: Job) => {
                let name = 'Unassigned';
                if (agent_id !== -1) {
                  name = technicians?.find(tech => tech.id === agent_id)?.displayName || 'Unassigned';
                }

                return <td>{ name }</td>;
              },
              'feedback': ({ feedback, phase }: Job) => {
                return (
                  <td className='text-center'>
                    { (feedback && phase !== JobPhase.Cancelled) ? <FeedbackRating rating={feedback?.rating} /> : '' }
                    { (feedback === undefined && phase !== JobPhase.Cancelled) ? 'N/A' : '' }
                    { phase === JobPhase.Cancelled && <CBadge color={ JobPhaseColor[phase] || 'secondary'}>{ JobPhaseLabel[phase] }</CBadge>}
                  </td>
                );
              },
              'wait_time': ({ metadata, arrived_time }: Job) => {
                const apptFrom = metadata.find(data => data.n === 'appt_from')?.v;
                if (!apptFrom || !arrived_time) {
                  return <td>N/A</td>;
                }
                const promisedTime = moment(parseInt(apptFrom as string, 10));
                const arrivedTime = moment(arrived_time);

                if (arrivedTime.isSameOrBefore(promisedTime)) {
                  return <td>On Time</td>;
                }

                return (
                  <td>{ diffAsString(promisedTime, arrivedTime) }</td>
                );
              },
              'actions': (job: Job) => {
                return (
                  <td style={{ width: '1%' }}>
                    <JobActions job={ job } />
                  </td>
                );
              },
              'description': (job: Job) => (
                <td>
                  <div className="line-clamp">
                    { formatJobDescription(job.description, job?.foreign_id, isSessionsEnabled)}
                  </div>
                </td>
              ),
              'details': (job: Job) => {
                if (showDrawer !== job?.invite_code) {
                  return null;
                }

                return (
                  <CCollapse show>
                    <JobsDetailsContainer job={job} columnSizes={COLUMN_SIZES} />
                  </CCollapse>
                );
              },
              'rank': (job: Job) => {
                return (
                  <td>{job?.rank >= 0 ? job?.rank : '-'}</td>
                );
              },
            }}
          />
        </div>
      </CCard>
    </div>
  );
};

export default CompletedJobList;
